import React, { useEffect, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { Section, SectionHeading } from "@/components/primitives";
import { api, formatApiErrorDetail, getSettings, trackEvent } from "@/lib/api";
import { toast } from "sonner";
import { EnvelopeSimple, ChatCircleDots } from "@phosphor-icons/react";

const CATEGORIES = ["Technology", "Membership", "Partnership", "Master Distributor", "Technical Support", "Compliance", "Media"];
const inputCls =
  "w-full rounded-lg border border-white/10 bg-[#08090F] px-4 py-3 text-white placeholder:text-[#475569] focus:border-[#2DD4BF]/50 focus:outline-none focus:ring-1 focus:ring-[#2DD4BF]/30 transition-colors";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm text-[#cbd5e1] mb-1.5">{label}</span>
      {children}
    </label>
  );
}

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", country: "", inquiry_type: "Technology", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    trackEvent("page_view", { page: "contact" });
    getSettings().then(setSettings).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      trackEvent("contact_submission", { type: form.inquiry_type });
      toast.success("Message sent. We'll get back to you.");
      setForm({ name: "", email: "", country: "", inquiry_type: "Technology", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Contact"
        title="Contact Quantum Scalp."
        sub="Questions about the technology, membership, partnership, or compliance? Reach out and select the right category."
      />
      <Section grid>
        <div className="grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2 space-y-6">
            <SectionHeading eyebrow="Get In Touch" title="We respond to every inquiry." />
            <div className="rounded-xl border border-white/10 bg-[#0A0B10] p-5 flex items-center gap-3">
              <EnvelopeSimple size={20} className="text-[#2DD4BF]" />
              <span className="text-sm text-[#cbd5e1]">{settings?.email || "support@quantumscalp.ai"}</span>
            </div>
            <div className="rounded-xl border border-white/10 bg-[#0A0B10] p-5 flex items-center gap-3">
              <ChatCircleDots size={20} className="text-[#2DD4BF]" />
              <span className="text-sm text-[#cbd5e1]">Live chat available on business days</span>
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-3 rounded-2xl border border-white/10 bg-[#0A0B10] p-6 md:p-8 space-y-4" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Name *">
                <input data-testid="contact-name" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" />
              </Field>
              <Field label="Email *">
                <input data-testid="contact-email" type="email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" />
              </Field>
              <Field label="Country">
                <input data-testid="contact-country" className={inputCls} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="Country" />
              </Field>
              <Field label="Inquiry Type">
                <select data-testid="contact-inquiry" className={inputCls} value={form.inquiry_type} onChange={(e) => setForm({ ...form, inquiry_type: e.target.value })}>
                  {CATEGORIES.map((c) => (
                    <option key={c} className="bg-[#08090F]">{c}</option>
                  ))}
                </select>
              </Field>
            </div>
            <Field label="Message *">
              <textarea data-testid="contact-message" rows={5} className={inputCls} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="How can we help?" />
            </Field>
            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit"
              className="w-full px-6 py-3.5 rounded-full bg-[#2DD4BF] text-[#030305] font-semibold hover:bg-white transition-colors disabled:opacity-60"
            >
              {submitting ? "Sending…" : "Send Message"}
            </button>
          </form>
        </div>
      </Section>
    </div>
  );
}
